import { ref, reactive, computed, type Ref } from "vue";
import {
  ChainInput,
  DssConnectionSettingInput,
} from "@/generated/graphql";
import {
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { ChainColumnList } from "@/common/constant";
import type { RadioOption } from "../types";

/** チェーン登録 フォーム管理に必要な依存をまとめた型。 */
interface UseChainRegisterFormDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  wordList: Ref<Record<string, string>>;
  chainListItems: Ref<
    ({ __typename?: "Chain"; chain_code: string; name: string } | null | undefined)[] | null
  >;
  getChainCountOverCustomerData: (chainCode: string, customerCode: string) => Promise<number>;
}

/**
 * チェーン登録 フォーム入力state管理を扱う composable。
 * チェーン登録入力・DSS接続設定入力・ラジオボタン・ハウスカード制御・blur時バリデーションを管理する。
 */
export const useChainRegisterForm = (deps: UseChainRegisterFormDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    wordList,
    chainListItems,
    getChainCountOverCustomerData,
  } = deps;

  // --- reactive / ref state ---
  const chainCreateInput = reactive<ChainInput>({
    chain_code: "",
    customer_code: "",
    max_unit: 99,
    house_card_display: 0,
    house_card_required: "",
    house_card_digit: "",
    house_card_format: "",
    operator_column_email: 0,
    name: "",
    user_id: "",
  });

  const dssConnectionSettingInput = reactive<DssConnectionSettingInput>({
    chain_code: "",
    customer_code: "",
    connection_name: "",
    dss_chain_store_code: "",
    dss_api_base_url: "",
    access_key: "",
    secret_key: "",
    user_id: "",
  });

  // ラジオボタン state
  const houseCardDisplayRadio = ref<RadioOption>({ id: 0 });
  const houseCardRequiredRadio = ref<RadioOption>({ id: 0 });
  const operatorColumnEmailRadio = ref<RadioOption>({ id: 0 });

  // ハウスカード入力制御
  const houseCardDisabled = ref(true);

  // ラジオ・セレクトの選択肢
  const houseCardDisplayRadioItems = ChainColumnList.house_card_display;
  const houseCardRequiredRadioItems = ChainColumnList.house_card_required;
  const operatorColumnEmailRadioItems = ChainColumnList.operator_column_email;

  const formInitialValues = computed(() => ({
    chain_code: chainCreateInput.chain_code,
    chain_name: chainCreateInput.name,
    house_card_display: houseCardDisplayRadio.value,
    house_card_required: houseCardRequiredRadio.value,
    house_card_digit: chainCreateInput.house_card_digit,
    house_card_format: chainCreateInput.house_card_format,
    operator_column_email: operatorColumnEmailRadio.value,
    dss_connection_name: dssConnectionSettingInput.connection_name,
    dss_chain_store_code: dssConnectionSettingInput.dss_chain_store_code,
    dss_api_base_url: dssConnectionSettingInput.dss_api_base_url,
    access_key: dssConnectionSettingInput.access_key,
    secret_key: dssConnectionSettingInput.secret_key,
  }));

  const isRequiredFieldsMissing = computed(() => {
    const requiredTextValues = [
      chainCreateInput.chain_code,
      chainCreateInput.name,
      dssConnectionSettingInput.connection_name,
      dssConnectionSettingInput.dss_chain_store_code,
      dssConnectionSettingInput.dss_api_base_url,
      dssConnectionSettingInput.access_key,
      dssConnectionSettingInput.secret_key,
    ];
    return requiredTextValues.some((value) => !value || value.trim() === "");
  });

  /**
   * ハウスカード登録ラジオ選択時のハウスカード入力制御切替。
   */
  const onClickHouseCard = (id: number): void => {
    houseCardDisabled.value = id === 0;
  };

  /**
   * チェーンコード @blur: 形式チェック（半角英小文字/数字/ハイフン/アンダーバー）+ API重複チェック。
   */
  const onBlurChainCd = async (): Promise<void> => {
    const code = chainCreateInput.chain_code;
    if (code) {
      overlay.value = true;
      // 英小文字・数字・ハイフン・アンダーバーのみ許可
      try {
        const regex = /^[0-9a-z-_]+$/;
        if (!regex.test(code)) {
          errdialog.value = true;
          errMessage.value =
            "チェーンコードに使用できる文字は、半角英小文字、半角数字、及びハイフンとアンダーバーです。";
          return;
        }
      } catch (err: unknown) {
        errdialog.value = true;
        errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      } finally {
        overlay.value = false;
      }

      overlay.value = true;
      try {
        const count = await getChainCountOverCustomerData(
          code,
          localStorageGetCustomerCode()
        );
        if (count > 0) {
          errdialog.value = true;
          errMessage.value = `チェーンコード ${code} は既に存在しています。`;
          chainCreateInput.chain_code = "";
          return;
        }
      } catch (err: unknown) {
        errdialog.value = true;
        errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      } finally {
        overlay.value = false;
      }
    }
  };

  /**
   * チェーン名 @blur: 既存チェーンリストとの名前重複チェック。
   */
  const onBlurChainName = async (): Promise<void> => {
    //チェーン名が入力されていたら重複チェック
    const name = chainCreateInput.name;
    if (name && chainListItems.value) {
      overlay.value = true;
      try {
        for (const item of chainListItems.value) {
          if (item?.name === name) {
            errdialog.value = true;
            errMessage.value = `チェーン名 ${name} は既に存在しています。`;
            chainCreateInput.name = "";
            break;
          }
        }
      } catch (err: unknown) {
        errdialog.value = true;
        errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      } finally {
        overlay.value = false;
      }
    }
  };

  /**
   * DSS URL @blur: URL形式バリデーション。
   */
  const onBlurDssUrl = async (): Promise<void> => {
    const url = dssConnectionSettingInput.dss_api_base_url;
    if (url) {
      overlay.value = true;
      try {
        const reg = new RegExp(
          "^([a-zA-Z]+:\\/\\/)?" + //プロトコルパターン
          "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + //ドメインパターン
          "((\\d{1,3}\\.){3}\\d{1,3}))" + //IPアドレスパターン
          "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // ポート番号またはパス
          "(\\?[;&a-z\\d%_.~+=-]*)?" + //クエリストリング
          "(\\#[-a-z\\d_]*)?", //フラグ情報
          "i"
        );
        if (!reg.test(url)) {
          errdialog.value = true;
          errMessage.value = "DSS URL に入力されたURLは正しくありません。";
          dssConnectionSettingInput.dss_api_base_url = "";
          return;
        }
      } catch (err: unknown) {
        errdialog.value = true;
        errMessage.value = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      } finally {
        overlay.value = false;
      }
    }
  };

  /**
   * 完了後のフォーム全項目クリア。
   */
  const resetForm = (): void => {
    // 入力項目クリア
    chainCreateInput.chain_code = "";
    chainCreateInput.customer_code = localStorageGetCustomerCode();
    chainCreateInput.max_unit = 99;
    chainCreateInput.house_card_display = 0;
    chainCreateInput.house_card_required = "";
    chainCreateInput.house_card_digit = "";
    chainCreateInput.house_card_format = "";
    chainCreateInput.operator_column_email = 0;
    chainCreateInput.name = "";

    dssConnectionSettingInput.chain_code = "";
    dssConnectionSettingInput.connection_name = "";
    dssConnectionSettingInput.dss_chain_store_code = "";
    dssConnectionSettingInput.dss_api_base_url = "";
    dssConnectionSettingInput.access_key = "";
    dssConnectionSettingInput.secret_key = "";
  };

  return {
    chainCreateInput,
    dssConnectionSettingInput,
    houseCardDisplayRadio,
    houseCardRequiredRadio,
    operatorColumnEmailRadio,
    houseCardDisabled,
    houseCardDisplayRadioItems,
    houseCardRequiredRadioItems,
    operatorColumnEmailRadioItems,
    formInitialValues,
    isRequiredFieldsMissing,
    onClickHouseCard,
    onBlurChainCd,
    onBlurChainName,
    onBlurDssUrl,
    resetForm,
  };
};
